import { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type {
  CopilotSketch,
  CopilotSketchPair,
} from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { styledSafe } from "src/utils/styledComponent";
import { zIndexLevel } from "src/utils/zIndexLevels";
import { OTHER_BRAND, sketchLegendOf, type SketchPalette } from "../store/sketchPalette";

/** "Your base" across the panel; each option half of it, side by side. */
export const BASE_WIDTH = 520;
export const GAP = 10;
export const OPTION_WIDTH = (BASE_WIDTH - GAP) / 2;
export const ENLARGED_WIDTH = 1000;
export const MIN_LABEL_WIDTH = 40;
const MIN_LABEL_HEIGHT = 12;
const CHAR_WIDTH = 6;

export type SketchTone = "light" | "dark";

interface Tone {
  wall: string;
  space: string;
  board: string;
  bay: string;
  edge: string;
  label: string;
  caption: string;
  text: string;
  card: string;
  border: string;
  scrim: string;
}

const TONES: Record<SketchTone, Tone> = {
  light: {
    wall: Color.lightGray,
    space: Color.white,
    board: Color.gray400,
    bay: Color.spaceGray,
    edge: Color.white,
    label: Color.textMain,
    caption: Color.textSecondary,
    text: Color.textMain,
    card: Color.white,
    border: Color.lightGrayHover,
    scrim: "rgba(0, 19, 10, 0.45)",
  },
  dark: {
    wall: Color.gray700,
    space: Color.gray800,
    board: Color.gray300,
    bay: Color.gray500,
    edge: Color.gray800,
    label: Color.textMain,
    caption: Color.gray300,
    text: Color.white,
    card: Color.gray800,
    border: Color.gray600,
    scrim: "rgba(0, 0, 0, 0.6)",
  },
};

const labelOf = (brand: string, width: number) => {
  const fits = Math.floor((width - 4) / CHAR_WIDTH);
  if (brand.length <= fits) return brand;
  return fits >= 3 ? `${brand.slice(0, fits - 1)}…` : null;
};

/** The wall a sketch stands on: its bays side by side, from the lowest shelf to the top of the bay. */
export const wallOf = (sketch: CopilotSketch) => {
  const xs = sketch.bays.flatMap((bay) => [bay.xLeft, bay.xRight]);
  const ys = sketch.shelves.flatMap((shelf) => [shelf.yBottom, shelf.yTop]);
  if (xs.length === 0 || ys.length === 0) return null;
  return {
    left: Math.min(...xs),
    right: Math.max(...xs),
    bottom: Math.min(...ys),
    top: Math.max(...ys),
  };
};

/** The pixel height a sketch `width` wide takes at the wall's own proportions. */
export const heightAt = (sketch: CopilotSketch, width: number) => {
  const wall = wallOf(sketch);
  if (!wall) return 0;
  return Math.round((width * (wall.top - wall.bottom)) / Math.max(1, wall.right - wall.left));
};

/**
 * The blocks that carry a label: of a brand's pieces cut at bay seams on one
 * shelf, only the widest, so a block across two bays is named once.
 */
export const labelledOf = (sketch: CopilotSketch): Set<number> => {
  const labelled = new Set<number>();
  let widest = -1;
  sketch.blocks.forEach((block, index) => {
    const before = sketch.blocks[index - 1];
    const joins =
      before !== undefined &&
      before.brand === block.brand &&
      before.yBottom === block.yBottom &&
      Math.abs(before.xRight - block.xLeft) <= 1e-6 * Math.max(1, Math.abs(block.xLeft));
    const width = (one: typeof block) => one.xRight - one.xLeft;
    if (joins && width(block) > width(sketch.blocks[widest])) {
      labelled.delete(widest);
      labelled.add(index);
      widest = index;
    } else if (!joins) {
      labelled.add(index);
      widest = index;
    }
  });
  return labelled;
};

interface DrawingProps {
  sketch: CopilotSketch;
  palette: SketchPalette;
  tone: Tone;
  width: number;
  labels?: boolean;
}

/**
 * The wall at its real proportions: each bay's shelves as their merchandising
 * space over a shelf board, the brands' blocks on them, the bays outlined.
 */
const Drawing = ({ sketch, palette, tone, width, labels = false }: DrawingProps) => {
  const clipId = `o-sketch-clip-${useId().replace(/:/g, "")}`;
  const wall = wallOf(sketch);
  if (!wall) return null;
  const height = heightAt(sketch, width);
  const scale = width / Math.max(1, wall.right - wall.left);
  const box = (xLeft: number, xRight: number, yBottom: number, yTop: number) => ({
    x: (xLeft - wall.left) * scale,
    y: (wall.top - yTop) * scale,
    width: Math.max(0, (xRight - xLeft) * scale),
    height: Math.max(0, (yTop - yBottom) * scale),
  });
  const board = Math.max(1, Math.min(3, width / 260));
  const named = labels ? labelledOf(sketch) : new Set<number>();

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={sketch.label}
      style={{ display: "block", background: tone.wall, maxWidth: "100%", height: "auto" }}
    >
      <defs>
        <clipPath id={clipId}>
          <rect width={width} height={height} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {sketch.shelves.map((shelf, index) => {
          const bay = sketch.bays[shelf.bay];
          if (!bay) return null;
          const at = box(bay.xLeft, bay.xRight, shelf.yBottom, shelf.yTop);
          return (
            <g key={`shelf-${index}`}>
              <rect {...at} fill={tone.space} data-testid="o-sketch-shelf" />
              <rect
                x={at.x}
                y={at.y + at.height - board / 2}
                width={at.width}
                height={board}
                fill={tone.board}
              />
            </g>
          );
        })}
        {sketch.blocks.map((block, index) => {
          const at = box(block.xLeft, block.xRight, block.yBottom, block.yTop);
          const label =
            named.has(index) &&
            block.brand !== OTHER_BRAND &&
            at.width >= MIN_LABEL_WIDTH &&
            at.height >= MIN_LABEL_HEIGHT
              ? labelOf(block.brand, at.width)
              : null;
          return (
            <g key={`block-${index}`}>
              <rect
                {...at}
                fill={palette(block)}
                stroke={tone.edge}
                strokeWidth={labels ? 1 : 0.5}
                data-testid="o-sketch-block"
                data-brand={block.brand}
              >
                <title>{block.brand}</title>
              </rect>
              {label && (
                <text
                  x={at.x + at.width / 2}
                  y={at.y + at.height / 2}
                  fill={tone.label}
                  fontSize="10"
                  textAnchor="middle"
                  dominantBaseline="central"
                  pointerEvents="none"
                  data-testid="o-sketch-label"
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}
        {sketch.bays.map((bay, index) => (
          <rect
            key={`bay-${index}`}
            {...box(bay.xLeft, bay.xRight, wall.bottom, wall.top)}
            fill="none"
            stroke={tone.bay}
            strokeWidth="1"
            data-testid="o-sketch-bay"
          />
        ))}
      </g>
    </svg>
  );
};

const Thumb = styledSafe("button")<{ ring: string; border: string }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: zoom-in;

  & > svg {
    border: 1px solid ${({ border }) => border};
    border-radius: 3px;
  }

  &:focus-visible {
    outline: 2px solid ${({ ring }) => ring};
    outline-offset: 2px;
  }
`;

const Scrim = styledSafe("div")<{ scrim: string }>`
  position: fixed;
  inset: 0;
  z-index: ${zIndexLevel.L12};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${({ scrim }) => scrim};
`;

const Card = styledSafe("div")<{ card: string; border: string }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(${ENLARGED_WIDTH + 24}px, 100%);
  max-height: 100%;
  overflow: auto;
  padding: 12px;
  border: 1px solid ${({ border }) => border};
  border-radius: 6px;
  background: ${({ card }) => card};
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
`;

const Close = styledSafe("button")<{ color: string }>`
  padding: 0 4px;
  border: none;
  background: none;
  color: ${({ color }) => color};
  font-size: 18px;
  line-height: 18px;
  cursor: pointer;
`;

const Swatch = styledSafe("span")<{ fill: string }>`
  display: inline-block;
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ fill }) => fill};
`;

interface LegendProps {
  sketches: readonly CopilotSketch[];
  palette: SketchPalette;
  tone: Tone;
}

const Legend = ({ sketches, palette, tone }: LegendProps) => {
  const { t } = useTranslation("common");
  const legend = useMemo(() => sketchLegendOf(sketches, palette), [sketches, palette]);
  if (legend.named.length === 0) return null;
  return (
    <Flex gap="4px 12px" flexWrap="wrap" align="center" data-testid="o-sketch-legend">
      {legend.named.map(({ brand, color }) => (
        <Flex key={brand} gap="4px" align="center" data-testid="o-sketch-legend-brand">
          <Swatch fill={color} aria-hidden="true" />
          <Text variant="small1" color={tone.text}>
            {brand}
          </Text>
        </Flex>
      ))}
      {legend.more > 0 && (
        <Text variant="small1" color={tone.caption}>
          <span data-testid="o-sketch-legend-more">
            {t("configCopilot.sketchMoreBrands", { count: legend.more })}
          </span>
        </Text>
      )}
    </Flex>
  );
};

interface Props {
  pair: CopilotSketchPair;
  palette: SketchPalette;
  tone?: SketchTone;
}

/** The pictures drawn across the top: the base as placed, or the base by blocking region. */
const LEADS = new Set(["base", "regions"]);

const namesOf = (sketch: CopilotSketch) => new Set(sketch.blocks.map((block) => block.brand));

/**
 * The pictures each legend names: one for all where the top picture shows the
 * options' brands; where it names what none of them shows (the base by
 * blocking region), its own legend, and one for the options' brands.
 */
export const legendGroupsOf = (
  lead: CopilotSketch | undefined,
  others: readonly CopilotSketch[],
): CopilotSketch[][] => {
  if (!lead) return [[...others]];
  const own = namesOf(lead);
  const shared = others.some((sketch) => [...namesOf(sketch)].some((name) => own.has(name)));
  return shared || others.length === 0 ? [[lead, ...others]] : [[lead], [...others]];
};

/**
 * The base as placed across the top, each option's rough re-pack below it,
 * each with the legend of what it shows; a click opens a sketch large with what it costs.
 */
export const OSketch = ({ pair, palette, tone: toneName = "light" }: Props) => {
  const { t } = useTranslation("common");
  const tone = TONES[toneName];
  const [opened, setOpened] = useState<number | null>(null);
  const shown = opened === null ? undefined : pair.options[opened];

  useEffect(() => {
    if (opened === null) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpened(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [opened]);

  if (pair.options.length === 0) return null;

  const thumb = (sketch: CopilotSketch, index: number, width: number) => (
    <Thumb
      key={`${sketch.option}-${index}`}
      type="button"
      ring={Color.primaryActive}
      border={tone.border}
      aria-haspopup="dialog"
      aria-expanded={opened === index}
      aria-label={t("configCopilot.sketchEnlarge", { label: sketch.label })}
      data-testid="o-sketch-option"
      data-option={sketch.option}
      style={{ width, maxWidth: "100%" }}
      onClick={() => setOpened(index)}
    >
      <Drawing sketch={sketch} palette={palette} tone={tone} width={width} />
      <Text variant="small1" color={tone.caption}>
        {sketch.caption}
      </Text>
    </Thumb>
  );

  const baseIndex = pair.options.findIndex((sketch) => LEADS.has(sketch.option));
  const others = pair.options
    .map((sketch, index) => ({ sketch, index }))
    .filter(({ index }) => index !== baseIndex);
  const groups = legendGroupsOf(
    pair.options[baseIndex],
    others.map(({ sketch }) => sketch),
  );
  const split = groups.length > 1;
  const legendOf = (sketch: CopilotSketch) =>
    groups.find((group) => group.includes(sketch)) ?? pair.options;

  return (
    <div
      style={{ width: BASE_WIDTH, maxWidth: "100%" }}
      data-testid="o-sketch"
      data-tone={toneName}
    >
      <Flex column gap={`${GAP}px`}>
        {baseIndex !== -1 && thumb(pair.options[baseIndex], baseIndex, BASE_WIDTH)}
        {split && <Legend sketches={groups[0]} palette={palette} tone={tone} />}
        {others.length > 0 && (
          <Flex gap={`${GAP}px`} align="start">
            {others.map(({ sketch, index }) =>
              thumb(sketch, index, others.length === 1 ? BASE_WIDTH : OPTION_WIDTH),
            )}
          </Flex>
        )}
        <Legend sketches={groups[groups.length - 1]} palette={palette} tone={tone} />
      </Flex>

      {shown &&
        createPortal(
          <Scrim
            scrim={tone.scrim}
            data-testid="o-sketch-scrim"
            onMouseDown={(event) => event.target === event.currentTarget && setOpened(null)}
          >
            <Card
              role="dialog"
              aria-modal="true"
              aria-label={shown.caption}
              card={tone.card}
              border={tone.border}
              data-testid="o-sketch-popover"
            >
              <Flex justify="between" align="center" gap="8px">
                <Text variant="body2" color={tone.text}>
                  {shown.label}
                </Text>
                <Close
                  type="button"
                  color={tone.caption}
                  aria-label={t("configCopilot.close")}
                  onClick={() => setOpened(null)}
                >
                  ×
                </Close>
              </Flex>
              <Drawing sketch={shown} palette={palette} tone={tone} width={ENLARGED_WIDTH} labels />
              <Text variant="small1" color={tone.caption}>
                {shown.caption}
              </Text>
              <Legend sketches={legendOf(shown)} palette={palette} tone={tone} />
              {pair.downstream && shown.option !== "base" && (
                <Text variant="small1" color={tone.text}>
                  <span data-testid="o-sketch-downstream">{pair.downstream}</span>
                </Text>
              )}
            </Card>
          </Scrim>,
          document.body,
        )}
    </div>
  );
};
